import { AD_DURATIONS, type AdDurationType, getTierForDuration } from '../../../utils/domain/ads/adDuration';

const isAnalyticsAdDurationTier = (value: string): value is AdDurationType =>
  (AD_DURATIONS as readonly string[]).includes(value);

const parseAdDurationToSeconds = (adDuration: string): number | null => {
  const trimmed = adDuration.trim();

  const colonShortMatch = /^:?(\d{1,2})$/.exec(trimmed);
  if (colonShortMatch) {
    return Number(colonShortMatch[1]);
  }

  const secondsSuffixMatch = /^(\d+)s$/i.exec(trimmed);
  if (secondsSuffixMatch) {
    return Number(secondsSuffixMatch[1]);
  }

  const minutesSecondsMatch = /^(\d{1,2}):(\d{2})(?:\.(\d+))?$/.exec(trimmed);
  if (minutesSecondsMatch) {
    const minutes = Number(minutesSecondsMatch[1]);
    const seconds = Number(minutesSecondsMatch[2]);
    return minutes * 60 + seconds;
  }

  const timecodeMatch = /^(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/.exec(trimmed);
  if (timecodeMatch) {
    const hours = Number(timecodeMatch[1]);
    const minutes = Number(timecodeMatch[2]);
    const seconds = Number(timecodeMatch[3]);
    return hours * 3600 + minutes * 60 + seconds;
  }

  const numericSeconds = Number(trimmed);
  if (!Number.isNaN(numericSeconds) && trimmed.match(/^\d+(\.\d+)?$/)) {
    return numericSeconds;
  }

  return null;
};

export const normalizeAnalyticsAdDuration = (adDuration?: string | null): string | undefined => {
  if (!adDuration?.trim()) {
    return undefined;
  }

  const trimmed = adDuration.trim();

  if (isAnalyticsAdDurationTier(trimmed)) {
    return trimmed;
  }

  const seconds = parseAdDurationToSeconds(trimmed);
  if (seconds == null) {
    return trimmed;
  }

  return getTierForDuration(seconds) ?? trimmed;
};
